import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { supabase } from '../lib/supabase';

interface Borrower {
  id: string;
  name: string;
  mobile_no: string;
  address: string;
}

interface BorrowerSearchAutocompleteProps {
  onBorrowerSelect: (borrowerId: string | null) => void;
}

const BorrowerSearchAutocomplete: React.FC<BorrowerSearchAutocompleteProps> = ({ onBorrowerSelect }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Borrower[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedBorrower, setSelectedBorrower] = useState<Borrower | null>(null);

  useEffect(() => {
    // Reset loading state when input is too short
    if (inputValue.length < 2) {
      setLoading(false);
      setOptions([]);
      return;
    }

    const fetchBorrowers = async () => {
      setLoading(true);
      try {
        // Direct query against borrowers - more efficient approach
        const { data, error } = await supabase
          .from('borrowers')
          .select('id, name, mobile_no, address')
          .ilike('name', `%${inputValue}%`)
          .is('deleted_at', null)
          .order('name')
          .limit(20);

        if (error) throw error;
        
        if (data && data.length > 0) {
          // Get the IDs of the borrowers we found
          const borrowerIds = data.map(b => b.id);
          
          // Check which of these borrowers are in for_collection
          const { data: collectionData, error: collectionError } = await supabase
            .from('for_collection')
            .select('borrower_id')
            .in('borrower_id', borrowerIds)
            .is('deleted_at', null);
            
          if (collectionError) throw collectionError;
          
          // Create a set of valid borrower IDs from collection data
          const validBorrowerIds = new Set(collectionData?.map(item => item.borrower_id) || []);
          
          // Filter the borrowers to only include those in the valid set
          const filteredBorrowers = data.filter(borrower => validBorrowerIds.has(borrower.id));
          
          setOptions(filteredBorrowers);
          
          // Log diagnostic info
          console.log(`Found ${filteredBorrowers.length} borrowers matching "${inputValue}" in collection list`);
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error('Error fetching borrowers:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchBorrowers, 300);
    return () => {
      clearTimeout(timeoutId);
      setLoading(false);
    };
  }, [inputValue]);

  return (
    <Autocomplete
      fullWidth
      options={options}
      getOptionLabel={(option) => option.name}
      filterOptions={(x) => x} // Disable client-side filtering
      loading={loading}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      onChange={(_, newValue) => {
        setSelectedBorrower(newValue);
        onBorrowerSelect(newValue ? newValue.id : null);
        if (!newValue) setInputValue('');
      }}
      value={selectedBorrower}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={inputValue.length < 2 ? "Type at least 2 characters to search" : "No borrowers found"}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search borrower by name..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(optionProps, option) => {
        // Properly extract the key prop
        const { key, ...props } = optionProps;
        return (
          <li key={key} {...props}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.mobile_no} • {option.address}
              </Typography>
            </Box>
          </li>
        );
      }}
    />
  );
};

export default BorrowerSearchAutocomplete; 